// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { Chip } from './Chip';

export const Toggle = ({ on, label, sub, locked }) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <div className="min-w-0">
      <div className="text-[12.5px] font-medium text-[#1F2937]">{label}</div>
      {sub && <div className="text-[11px] text-[#6B7280] mt-0.5">{sub}</div>}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {locked && <span className="text-[10.5px] text-[#9CA3AF] uppercase tracking-wider">Locked</span>}
      <div className={`w-8 h-4 rounded-full relative transition-colors ${on ? 'bg-[#1F2937]' : 'bg-[#D6D3D1]'} ${locked ? 'opacity-60' : ''}`}>
        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
      </div>
    </div>
  </div>
);

// Editable settings row — inline state with edit affordance

// `disabled` is a real state here, not decoration: a choice the system cannot honour must look and
// behave unavailable, and say why on hover, rather than being silently hidden (which leaves the next
// reader to rediscover the reason) or left clickable (which is how an operator ends up believing
// they chose something). `title` carries the reason — see reason-on-hover across the console.
export const RadioCard = ({ selected, onClick, label, sub, suffix, disabled = false, title }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    title={title || (disabled && typeof sub === 'string' ? sub : undefined)}
    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${
    disabled ? 'border-[#EEEDEA] bg-[#FBFAF7] cursor-not-allowed opacity-70'
    : selected ? 'border-[#1F2937] border-2 bg-[#FBFAF7]' : 'border-[#E6E4E1] bg-white hover:border-[#D6D3D1]'
  }`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`w-4 h-4 rounded-full mt-0.5 flex-shrink-0 flex items-center justify-center ${selected ? 'bg-[#1F2937]' : 'border-2 border-[#D6D3D1]'}`}>
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[13px] font-semibold ${disabled ? 'text-[#9CA3AF]' : 'text-[#1F2937]'}`}>{label}</div>
          {sub && <div className="text-[11.5px] text-[#6B7280] mt-0.5">{sub}</div>}
        </div>
      </div>
      {suffix && <div className="text-[12.5px] font-medium text-[#1F2937] flex-shrink-0">{suffix}</div>}
    </div>
  </button>
);

// Field label + input (form pattern)

// Every Field labels its control for real — a generated id flows onto the single
// form-control child (Input/Select/Textarea all spread ...rest onto their native
// element, so this needs no change at any of the hundreds of call sites) and the
// label becomes a real <label htmlFor>, not a bare <div> a screen reader skips.
export const Field = ({ label, optional, required, children, sub }) => {
  const generatedId = useId();
  const child = React.isValidElement(children) ? children : null;
  const controlId = child ? (child.props.id || generatedId) : undefined;
  const labelledChildren = child ? React.cloneElement(child, { id: controlId }) : children;
  return (
    <div>
      <label htmlFor={controlId} className="block text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">
        {label}
        {required && <span className="font-normal normal-case ml-1 text-[#9B1F1F]">*</span>}
        {optional && <span className="font-normal normal-case ml-1 text-[#9CA3AF]">(optional)</span>}
      </label>
      {labelledChildren}
      {sub && <div className="text-[11px] text-[#6B7280] mt-1">{sub}</div>}
    </div>
  );
};

// Text input — consistent with the rest of the system

export const Input = ({ value, onChange, placeholder, type = 'text', disabled, ...rest }) => (
  <input
    type={type}
    {...(onChange
      ? { value: value !== undefined ? value : '', onChange }
      : value !== undefined
        ? { value, readOnly: true }
        : {})}
    placeholder={placeholder}
    disabled={disabled}
    className={`w-full h-9 px-3 text-[12.5px] border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] ${disabled ? 'bg-[#FBFAF7] text-[#6B7280] cursor-not-allowed' : 'bg-white'}`}
    {...rest}
  />
);

// Textarea

// ⌘/Ctrl + Enter submits the composer a textarea belongs to.
//
// Plain Enter can't be the send key here: these are multi-line message composers, and an
// operator writing a two-line reply to a customer would fire it off half-written. The
// modified chord is the convention every chat tool the operator already uses shares
// (WhatsApp Web, Slack in send-on-⌘↵ mode, GitHub).
//
// `disabled` is not optional politeness — pass the SAME condition the Send button uses, so
// the shortcut can never fire a send the button itself refuses (empty body, request already
// in flight, closed session window). A shortcut that bypasses the button's guard is a way to
// double-send a real WhatsApp message to a real customer.
export const submitOnCmdEnter = (submit, { disabled = false } = {}) => (e) => {
  if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
  e.preventDefault();
  if (disabled || typeof submit !== 'function') return;
  submit();
};

export const Textarea = ({ value, onChange, placeholder, rows = 3, ...rest }) => (
  <textarea
    {...(onChange
      ? { value: value !== undefined ? value : '', onChange }
      : value !== undefined
        ? { value, readOnly: true }
        : {})}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-3 py-2 text-[12.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] resize-y"
    {...rest}
  />
);

// Select — native dropdown

export const Select = ({ value, defaultValue, onChange, options, placeholder, disabled, children, ...rest }) => {
  const baseProps = onChange ? { value: value !== undefined ? value : '', onChange } : { defaultValue };
  return (
    <div className="relative">
      <select {...baseProps} disabled={disabled} {...rest}
        className={`w-full h-9 pl-3 pr-9 text-[12.5px] border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] appearance-none cursor-pointer ${disabled ? 'bg-[#FBFAF7] text-[#6B7280]' : 'bg-white'}`}>
        {placeholder && <option value="">{placeholder}</option>}
        {options ? options.map((o, i) => typeof o === 'string' ? <option key={i} value={o}>{o}</option> : <option key={i} value={o.value}>{o.label}</option>) : children}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-[#6B7280] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
};

// Slider — range with label

export const Slider = ({ value, onChange, min = 0, max = 100, step = 1, suffix = '%' }) => (
  <div className="flex items-center gap-3">
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange && onChange(Number(e.target.value))}
      className="flex-1 h-1.5 bg-[#E6E4E1] rounded-full appearance-none cursor-pointer accent-[#1F2937]" />
    <div className="h-8 w-16 rounded-md border border-[#E6E4E1] bg-white text-[12.5px] text-[#1F2937] font-medium tabular-nums flex items-center justify-center flex-shrink-0">
      {value}{suffix}
    </div>
  </div>
);

// Stateful toggle — used in settings (visually identical to Toggle but clickable)

export const ToggleControl = ({ value, onChange, label, sub, locked, badge }) => (
  <button onClick={() => !locked && onChange && onChange(!value)} disabled={locked} className={`w-full flex items-start justify-between gap-3 px-4 py-3 rounded-md border ${value ? 'border-[#1F2937] bg-white' : 'border-[#E6E4E1] bg-white'} ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:border-[#D6D3D1]'} text-left transition-colors`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-[12.5px] font-medium text-[#1F2937]">{label}</span>
        {badge && <Chip tone="neutral" size="sm">{badge}</Chip>}
      </div>
      {sub && <div className="text-[11px] text-[#6B7280] mt-0.5">{sub}</div>}
    </div>
    <div className={`w-8 h-4 rounded-full relative transition-colors flex-shrink-0 mt-1 ${value ? 'bg-[#1F6A3A]' : 'bg-[#D6D3D1]'}`}>
      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${value ? 'left-[18px]' : 'left-0.5'}`} />
    </div>
  </button>
);


/* ============================================================================
   PHASE 3 — REFUND WIZARD + VIEW ORDER + FLAG TRANSACTION MODALS
   ============================================================================ */

